import type { LucideIcon } from 'lucide-react';
import { ArrowLeftRight, BookUser, CalendarRange, ChartColumn, ClipboardCheck, Contact, FileText, Landmark, PlaneTakeoff } from 'lucide-react';
import type { ModuleRole, PortalModule } from '../data/database.types';

/**
 * The portal's modules (V2 D83). A tile on the home screen, a route prefix,
 * and the roles a person can hold in it. Access is one role per person per
 * module, held in `member_module_access`; portal admins are admin everywhere.
 * A module that is not yet built stays registered so templates can grant it
 * ahead of time; only portal admins see its tile, marked as coming soon.
 */
export interface ModuleDef {
  key: PortalModule;
  label: string;
  icon: LucideIcon;
  path: string;
  enabled: boolean;
  /** The roles this module distinguishes, lowest first, with the label shown for each. */
  roles: { value: ModuleRole; label: string }[];
  /**
   * A module that lives inside another's pages (D104): it has its own roles
   * and grants but no tile of its own; a role in it opens the parent's tile
   * and its page is a tab there.
   */
  within?: PortalModule;
}

export const MODULES: readonly ModuleDef[] = [
  {
    key: 'scheduler',
    label: 'Scheduler',
    icon: CalendarRange,
    path: '/scheduler',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Pilot' },
      { value: 'editor', label: 'Scheduler' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: FileText,
    path: '/documents',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Reader' },
      { value: 'editor', label: 'Editor' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: ChartColumn,
    path: '/analytics',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Viewer' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'directory',
    label: 'Directory',
    icon: BookUser,
    path: '/directory',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Viewer' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'crewswap',
    label: 'Crew Swaps',
    icon: ArrowLeftRight,
    path: '/crewswap',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Crew' },
      { value: 'editor', label: 'Coordinator' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'finance',
    label: 'Financials',
    icon: Landmark,
    path: '/analytics/finance',
    enabled: true,
    within: 'analytics',
    roles: [
      { value: 'viewer', label: 'Viewer' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    icon: Contact,
    path: '/customers',
    enabled: true,
    roles: [
      { value: 'editor', label: 'Sales' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'expansion',
    label: 'Fleet Expansion',
    icon: PlaneTakeoff,
    path: '/expansion',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Viewer' },
      { value: 'editor', label: 'Analyst' },
      { value: 'admin', label: 'Admin' },
    ],
  },
  {
    key: 'onboarding',
    label: 'Onboarding',
    icon: ClipboardCheck,
    path: '/onboarding',
    enabled: true,
    roles: [
      { value: 'viewer', label: 'Owner' },
      { value: 'editor', label: 'Coordinator' },
      { value: 'admin', label: 'Admin' },
    ],
  },
];

/** The modules that open a tile for this person: their own, plus a parent whose embedded module they hold. */
export function visibleModules(access: Grants, portalAdmin: boolean): ModuleDef[] {
  return MODULES.filter((m) => !m.within && (portalAdmin || (m.enabled && (access[m.key] || MODULES.some((e) => e.within === m.key && e.enabled && access[e.key])))));
}

export type Grants = Partial<Record<PortalModule, ModuleRole>>;

export function moduleDef(key: PortalModule): ModuleDef {
  const def = MODULES.find((m) => m.key === key);
  if (!def) throw new Error(`unknown module ${key}`);
  return def;
}

export function roleLabel(key: PortalModule, role: ModuleRole | undefined): string {
  if (!role) return 'No access';
  return moduleDef(key).roles.find((r) => r.value === role)?.label ?? role;
}

/** Two grant sets are the same when every module agrees, absent meaning no access. */
export function sameGrants(a: Grants, b: Grants): boolean {
  return MODULES.every((m) => (a[m.key] ?? null) === (b[m.key] ?? null));
}

/** Grants as stored in `access_templates.grants` (an object of module to role). */
export function grantsFromJson(json: unknown): Grants {
  const out: Grants = {};
  if (json && typeof json === 'object' && !Array.isArray(json)) {
    for (const m of MODULES) {
      const v = (json as Record<string, unknown>)[m.key];
      if (typeof v === 'string' && m.roles.some((r) => r.value === v)) out[m.key] = v as ModuleRole;
    }
  }
  return out;
}
